import { CalendarOutlined, WarningOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Card, Flex, Progress, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TechnicianWorkload } from "../../generated-axios-client";
import { palette } from "../../theme";
import styles from "../Dashboard.module.css";

const DAYS_IN_WEEK = 7;

type Props = {
  intl: IntlShape;
  rows: TechnicianWorkload[] | undefined;
  loading: boolean;
  onOpenSchedule: () => void;
  onOpenTechnicianWorkOrders: (technicianId: number) => void;
};

const BookedWeek = ({
  intl,
  bookedDays,
  doubleBookedDays,
}: {
  intl: IntlShape;
  bookedDays: number;
  doubleBookedDays: number;
}) => {
  if (bookedDays === 0) {
    return (
      <Tag color="success">{intl.formatMessage({ id: "dashboard_workload_free" })}</Tag>
    );
  }
  return (
    <Flex align="center" gap="small">
      <Progress
        className={styles.workloadBar}
        percent={Math.round((bookedDays / DAYS_IN_WEEK) * 100)}
        showInfo={false}
        size="small"
        strokeColor={palette.dashBlue}
      />
      <Typography.Text>
        {intl.formatMessage(
          { id: "dashboard_workload_booked_days" },
          { days: bookedDays }
        )}
      </Typography.Text>
      {doubleBookedDays > 0 && (
        <Tooltip
          title={intl.formatMessage(
            { id: "dashboard_workload_double_booked" },
            { count: doubleBookedDays }
          )}
        >
          <Typography.Text type="warning">
            <WarningOutlined />
          </Typography.Text>
        </Tooltip>
      )}
    </Flex>
  );
};

export const TechnicianWorkloadTable = ({
  intl,
  rows,
  loading,
  onOpenSchedule,
  onOpenTechnicianWorkOrders,
}: Props) => {
  const columns: ColumnsType<TechnicianWorkload> = [
    {
      key: "technician",
      title: intl.formatMessage({ id: "dashboard_workload_column_technician" }),
      render: (_, row) => (
        <Flex gap="small" align="baseline">
          <Typography.Text>{row.technician_name}</Typography.Text>
          {row.technician_level && (
            <Typography.Text type="secondary">{row.technician_level}</Typography.Text>
          )}
        </Flex>
      ),
    },
    {
      key: "open",
      title: intl.formatMessage({ id: "dashboard_workload_column_open" }),
      render: (_, row) =>
        row.open_work_orders > 0 ? (
          <Typography.Link onClick={() => onOpenTechnicianWorkOrders(row.technician_id)}>
            {row.open_work_orders}
          </Typography.Link>
        ) : (
          <Typography.Text type="secondary">0</Typography.Text>
        ),
    },
    {
      key: "this_week",
      title: intl.formatMessage({ id: "dashboard_workload_column_this_week" }),
      render: (_, row) => (
        <BookedWeek
          intl={intl}
          bookedDays={row.booked_days_this_week}
          doubleBookedDays={row.double_booked_days_this_week}
        />
      ),
    },
    {
      key: "next_week",
      title: intl.formatMessage({ id: "dashboard_workload_column_next_week" }),
      render: (_, row) => (
        <BookedWeek
          intl={intl}
          bookedDays={row.booked_days_next_week}
          doubleBookedDays={row.double_booked_days_next_week}
        />
      ),
    },
  ];

  return (
    <Card
      className={styles.fullHeight}
      title={intl.formatMessage({ id: "dashboard_workload_title" })}
      extra={
        <Button size="small" icon={<CalendarOutlined />} onClick={onOpenSchedule}>
          {intl.formatMessage({ id: "dashboard_workload_open_schedule" })}
        </Button>
      }
    >
      <Table<TechnicianWorkload>
        rowKey="technician_id"
        size="small"
        columns={columns}
        dataSource={rows ?? []}
        loading={loading}
        pagination={{ pageSize: 8, hideOnSinglePage: true, size: "small" }}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: intl.formatMessage({ id: "dashboard_workload_empty" }) }}
      />
    </Card>
  );
};
